import type { ReactNode } from 'react';
import {
  IconReceivable, IconPayable, IconDoc, IconCard, IconReport, IconListBullet,
  IconLedger, IconService, IconPeople, IconUsers, IconProducts, IconCalendar, IconLink,
} from '@/components/ui/Icon';
import { FAVORITE_ENTITIES } from './favoriteEntities';

// Single source of truth: a distinct icon per record entity type (table /
// document) in the system, used wherever favorites are rendered (the Favorites
// widget tiles and the nav favorites dropdown). entity_type keys mirror
// document-lineage.js / favoriteEntities.ts. Add a row here when a new
// entity type becomes favoritable.
const ENTITY_ICON: Record<string, (size: number) => ReactNode> = {
  customer_invoice:     (s) => <IconReceivable size={s} />,
  vendor_invoice:       (s) => <IconPayable size={s} />,
  sales_order:          (s) => <IconDoc size={s} />,
  purchase_order:       (s) => <IconCard size={s} />,
  sales_quote:          (s) => <IconReport size={s} />,
  purchase_requisition: (s) => <IconListBullet size={s} />,
  journal_entry:        (s) => <IconLedger size={s} />,
  service_job:          (s) => <IconService size={s} />,
  customer:             (s) => <IconPeople size={s} />,
  vendor:               (s) => <IconUsers size={s} />,
  item:                 (s) => <IconProducts size={s} />,
  project:              (s) => <IconCalendar size={s} />,
};

// A starred PAGE (kind==='route') is usually an entity's list page — map its
// url to the same entity icon (e.g. '/customers' -> customer). Longest route
// first so a more specific prefix wins.
const ROUTE_ENTITY = FAVORITE_ENTITIES.slice().sort((a, b) => b.route.length - a.route.length);
function entityForUrl(url: string): string | undefined {
  return ROUTE_ENTITY.find((d) => url === d.route || url.startsWith(d.route + '/'))?.entity_type;
}

// Resolve the icon for a favorite: by its record entity type, or — for a starred
// page — by the entity its url belongs to, falling back to a generic link glyph.
export function favoriteIcon(f: { kind: 'record' | 'route'; entity_type?: string; url?: string }, size = 16): ReactNode {
  // A starred saved/built-in REPORT (the builder's drill routes) gets its own
  // glyph in a distinct color, so report tiles read apart from plain page
  // links at a glance.
  if (f.kind === 'route' && f.url && /^\/receivables\/reports\/./.test(f.url)) {
    return <IconReport size={size} className="text-emerald-600" />;
  }
  const type = f.kind === 'record' ? f.entity_type : (f.url ? entityForUrl(f.url) : undefined);
  if (type && ENTITY_ICON[type]) return ENTITY_ICON[type](size);
  return <IconLink size={size} />;
}
